/* eslint-disable react-hooks/set-state-in-effect */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import {
  colorEngine,
  type ColorResult,
  type ColorParseResult,
  type DetectedFormat,
  type CMYK,
} from '../lib/colorEngine';
import Color from 'colorjs.io';
import { usePantoneAuth } from '../hooks/usePantoneAuth';
import { useEyeDropper } from '../hooks/useEyeDropper';
import { usePrecisionSettings } from '../hooks/usePrecisionSettings';
import { useColorHistory } from '../hooks/useColorHistory';
import { useColorFavorites } from '../hooks/useColorFavorites';
import { useKeyboardShortcutsContext } from './KeyboardShortcutsContext';
import { useIccProfiles } from '../hooks/useIccProfiles';
import { iccManager } from '../lib/icc/iccManager';
import { DEFAULT_PRECISION_SETTINGS } from '../lib/precisionSettings';

// ── Format label helper ────────────────────────────────────────────────
const FORMAT_LABELS: Record<DetectedFormat, string> = {
  hex: 'HEX', rgb: 'RGB', rgba: 'RGBA', hsl: 'HSL', hsla: 'HSLA',
  hsv: 'HSV', hwb: 'HWB', lab: 'CIELAB', lch: 'CIELCH', oklab: 'OKLab',
  oklch: 'OKLCH', cmyk: 'CMYK', 'css-named': 'CSS Named Color',
  pantone: 'Pantone', p3: 'Display-P3', rec2020: 'Rec.2020', unknown: 'Unknown',
};

// ── Context shape ──────────────────────────────────────────────────────
export interface ColorContextType {
  // Input
  input: string;
  setInput: (value: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;

  // Parsed results
  result: ColorResult | null;
  displayResult: ColorResult | null;
  parseResult: ColorParseResult | null;
  error: string | null;

  // Validation
  getFormatLabel: (format: DetectedFormat) => string;

  // Gamut
  selectedGamut: 'srgb' | 'p3' | 'rec2020';
  setSelectedGamut: (gamut: 'srgb' | 'p3' | 'rec2020') => void;

  // CMYK profiles
  selectedCmykProfile: string;
  handleSelectProfile: (profileId: string) => void;

  // Favorites
  isFavorited: boolean;
  toggleFavorite: () => Promise<void>;

  // View mode
  viewMode: 'single' | 'batch';
  setViewMode: (mode: 'single' | 'batch') => void;

  // Clipboard
  copiedField: string | null;
  copyToClipboard: (text: string, field: string) => void;

  // EyeDropper
  eyeDropperSupported: boolean;
  openEyeDropper: () => Promise<string | null>;
  eyeDropperError: string | null;

  // Formatting utilities
  applyDecimalPrecision: (value: number) => string;
  formatCmyk: (values: CMYK) => CMYK;
  formatValue: (value: unknown, depth: number) => string;
  getCssFunctional: (key: keyof ColorResult | 'cmykActive', value: unknown) => string;

  // Pantone
  pantoneState: ReturnType<typeof usePantoneAuth>['state'];

  // Precision settings
  precisionSettings: typeof DEFAULT_PRECISION_SETTINGS;

  // Gamut preview
  gamutPreview: { color: string; label: string; warning: string | null };
}

const ColorContext = createContext<ColorContextType | undefined>(undefined);

// ── Provider ───────────────────────────────────────────────────────────
export function ColorProvider({ children }: { children: ReactNode }) {
  const [input, setInput] = useState('#3b82f6');
  const [result, setResult] = useState<ColorResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<ColorParseResult | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [selectedGamut, setSelectedGamut] = useState<'srgb' | 'p3' | 'rec2020'>('srgb');
  const [selectedCmykProfile, setSelectedCmykProfile] = useState<string>('generic');
  const [viewMode, setViewMode] = useState<'single' | 'batch'>('single');
  const [isFavorited, setIsFavorited] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { state: pantoneState } = usePantoneAuth();
  const iccSnapshot = useIccProfiles();
  const lastParsedInput = useRef<string>('');
  const iccManagerActiveRef = useRef<string | null>(null);

  const {
    isSupported: eyeDropperSupported,
    open: openEyeDropper,
    error: eyeDropperError,
  } = useEyeDropper();

  const { addToHistory } = useColorHistory();
  const {
    addFavorite,
    removeFavorite,
    isFavorite: checkIsFavorite,
    getFavoriteByHex,
  } = useColorFavorites();

  const {
    registerShortcuts,
    unregisterShortcuts,
    setLastConvertedColor,
    toggleHelp,
  } = useKeyboardShortcutsContext();

  const { state: precisionState } = usePrecisionSettings();
  const precisionSettings = precisionState?.settings ?? DEFAULT_PRECISION_SETTINGS;

  const clampTarget = useMemo<'none' | 'srgb' | 'p3' | 'rec2020'>(
    () =>
      precisionSettings.gamutClampMode === 'auto'
        ? selectedGamut
        : precisionSettings.gamutClampMode,
    [precisionSettings.gamutClampMode, selectedGamut],
  );

  const renderResult = useMemo(() => {
    if (!result) return null;
    if (clampTarget === 'none') return result;
    const clampedHex = colorEngine.clampHexToGamut(result.hex, clampTarget);
    if (!clampedHex || clampedHex.toLowerCase() === result.hex.toLowerCase()) return result;
    return colorEngine.parseColor(clampedHex) ?? result;
  }, [clampTarget, result]);

  // ── Effects (moved verbatim from ColorConverter) ─────────────────────
  useEffect(() => {
    let isMounted = true;
    if (result?.hex) {
      void checkIsFavorite(result.hex).then((favorited) => {
        if (isMounted) setIsFavorited(favorited);
      });
    } else {
      if (isMounted) setIsFavorited(false);
    }
    return () => { isMounted = false; };
  }, [result?.hex, checkIsFavorite]);

  useEffect(() => {
    if (result?.hex && lastParsedInput.current !== input.trim()) {
      void addToHistory(result.hex, 'hex', result.hex, 'manual');
    }
  }, [result?.hex, input, addToHistory]);

  useEffect(() => {
    if (result?.hex) setLastConvertedColor(result.hex);
  }, [result?.hex, setLastConvertedColor]);

  useEffect(() => {
    const shortcuts = [
      {
        key: 'c', ctrlOrCmd: true,
        description: 'Copy current color', category: 'Color Conversion',
        handler: () => {
          if (result?.hex) {
            navigator.clipboard.writeText(result.hex);
            setCopiedField('hex');
            setTimeout(() => setCopiedField(null), 2000);
          }
        },
      },
      {
        key: 'v', ctrlOrCmd: true,
        description: 'Paste color into input field', category: 'Color Conversion',
        handler: async () => {
          try {
            const text = await navigator.clipboard.readText();
            if (text) { setInput(text); inputRef.current?.focus(); }
          } catch (err) { console.error('Failed to read clipboard:', err); }
        },
      },
      {
        key: 'k', ctrlOrCmd: true,
        description: 'Focus color input field', category: 'Color Conversion',
        handler: () => { inputRef.current?.focus(); },
      },
      {
        key: '/', ctrlOrCmd: true,
        description: 'Show keyboard shortcuts help', category: 'General',
        handler: () => { toggleHelp(); },
      },
    ];
    registerShortcuts(shortcuts);
    return () => { unregisterShortcuts(shortcuts.map((s) => s.key)); };
  }, [result?.hex, registerShortcuts, unregisterShortcuts, toggleHelp]);

  // Parse color input
  useEffect(() => {
    const trimmed = input.trim();
    if (!trimmed) {
      setResult(null); setError(null); setParseResult(null);
      setSelectedGamut('srgb'); setSelectedCmykProfile('generic');
      return;
    }
    const pr = colorEngine.parseColorWithFeedback(trimmed);
    setParseResult(pr);
    if (pr.success && pr.result) {
      const parsed = pr.result;
      setResult(parsed); setError(null);
      if (lastParsedInput.current !== trimmed) setSelectedGamut('srgb');
      const profiles = parsed.cmykProfiles ?? [];
      const managedProfileId = profiles.find((p) => p.id.startsWith('icc:'))?.id ?? null;
      const hasActivePlugin = Boolean(
        iccSnapshot.activeProfileId && profiles.some((p) => p.id === iccSnapshot.activeProfileId),
      );
      setSelectedCmykProfile((prev) => {
        if (hasActivePlugin) return iccSnapshot.activeProfileId ?? prev;
        if (managedProfileId && prev === managedProfileId) return prev;
        return 'generic';
      });
      lastParsedInput.current = trimmed;
    } else {
      setError(pr.error ?? 'Invalid color format'); setResult(null);
    }
  }, [input, iccSnapshot.activeProfileId]);

  useEffect(() => {
    if (!input.trim()) return;
    const parsed = colorEngine.parseColor(input.trim());
    if (parsed) setResult(parsed);
  }, [iccSnapshot.revision, input]);

  // ── Callbacks ────────────────────────────────────────────────────────
  const copyToClipboard = useCallback((text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  const getFormatLabel = useCallback((format: DetectedFormat): string => FORMAT_LABELS[format], []);

  const toggleFavorite = useCallback(async () => {
    if (!result?.hex) return;
    try {
      if (isFavorited) {
        const favorite = getFavoriteByHex(result.hex);
        if (favorite?.id) await removeFavorite(favorite.id);
      } else {
        await addFavorite(result.hex);
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  }, [result?.hex, isFavorited, getFavoriteByHex, removeFavorite, addFavorite]);

  const applyDecimalPrecision = useCallback(
    (value: number): string => {
      if (!Number.isFinite(value)) return '—';
      const decimals = Math.max(0, precisionSettings.decimalPlaces);
      const factor = 10 ** decimals;
      const rounded = Math.round(value * factor) / factor;
      return decimals === 0 ? Math.round(rounded).toString() : rounded.toFixed(decimals);
    },
    [precisionSettings.decimalPlaces],
  );

  const formatCmyk = useCallback(
    (values: CMYK): CMYK => {
      const step = precisionSettings.cmykStep;
      const roundCh = (ch: number) => {
        const n = Number.isFinite(ch) ? ch : 0;
        if (step <= 0) return n;
        return Math.min(Math.max(Math.round(n / step) * step, 0), 100);
      };
      return { c: roundCh(values.c), m: roundCh(values.m), y: roundCh(values.y), k: roundCh(values.k) } satisfies CMYK;
    },
    [precisionSettings.cmykStep],
  );

  const formatValue = useMemo<(value: unknown, depth: number) => string>(() => {
    const inner = (value: unknown, depth: number = 0): string => {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'number') return applyDecimalPrecision(value);
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) return value.map((e) => inner(e, depth + 1)).join(', ');
      if (typeof value === 'object') {
        if (['c', 'm', 'y', 'k'].every((k) => k in (value as Record<string, unknown>))) {
          const r = formatCmyk(value as CMYK);
          return JSON.stringify([r.c, r.m, r.y, r.k]);
        }
        return Object.entries(value).map(([k, v]) => `${k}: ${inner(v, depth + 1)}`).join(depth === 0 ? '\n' : ', ');
      }
      return String(value);
    };
    return inner;
  }, [applyDecimalPrecision, formatCmyk]);

  const handleSelectProfile = useCallback((profileId: string) => {
    setSelectedCmykProfile(profileId);
    if (profileId.startsWith('icc:')) {
      iccManager.setActiveProfile(profileId);
      iccManagerActiveRef.current = profileId;
    } else {
      iccManager.setActiveProfile(null);
      iccManagerActiveRef.current = null;
    }
  }, []);

  useEffect(() => {
    iccManagerActiveRef.current = iccSnapshot.activeProfileId ?? null;
  }, [iccSnapshot.activeProfileId]);

  const displayResult = renderResult ?? result;

  const getCssFunctional = useCallback(
    (key: keyof ColorResult | 'cmykActive', value: unknown): string => {
      if (!displayResult || value === undefined || value === null) return '';
      const r = displayResult.rgb.r;
      const g = displayResult.rgb.g;
      const b = displayResult.rgb.b;
      const a = displayResult.rgb.a;
      const fmt = (n: number) => applyDecimalPrecision(n);

      switch (key) {
        case 'hex': return displayResult.hex;
        case 'rgb':
          return a !== undefined ? `rgb(${Math.round(r)} ${Math.round(g)} ${Math.round(b)} / ${fmt(a)})` : `rgb(${Math.round(r)} ${Math.round(g)} ${Math.round(b)})`;
        case 'hsl': {
          const hsl = displayResult.hsl;
          return hsl.a !== undefined
            ? `hsl(${Math.round(hsl.h)} ${Math.round(hsl.s)}% ${Math.round(hsl.l)}% / ${fmt(hsl.a)})`
            : `hsl(${Math.round(hsl.h)} ${Math.round(hsl.s)}% ${Math.round(hsl.l)}%)`;
        }
        case 'hwb': {
          const hwb = displayResult.hwb;
          return hwb.a !== undefined
            ? `hwb(${Math.round(hwb.h)} ${Math.round(hwb.w)}% ${Math.round(hwb.b)}% / ${fmt(hwb.a)})`
            : `hwb(${Math.round(hwb.h)} ${Math.round(hwb.w)}% ${Math.round(hwb.b)}%)`;
        }
        case 'lab': { const l = displayResult.lab; return `lab(${fmt(l.l)} ${fmt(l.a)} ${fmt(l.b)})`; }
        case 'lch': { const l = displayResult.lch; return `lch(${fmt(l.l)} ${fmt(l.c)} ${fmt(l.h)})`; }
        case 'oklab': { const o = displayResult.oklab; return `oklab(${fmt(o.l)} ${fmt(o.a)} ${fmt(o.b)})`; }
        case 'oklch': { const o = displayResult.oklch; return `oklch(${fmt(o.l)} ${fmt(o.c)} ${fmt(o.h)})`; }
        case 'displayP3':
          if (!displayResult.displayP3) return '';
          { const c = new Color(displayResult.hex); const p3 = c.to('p3'); const [pr,pg,pb] = p3.coords; return p3.alpha !== 1 ? `color(display-p3 ${fmt(pr)} ${fmt(pg)} ${fmt(pb)} / ${fmt(p3.alpha)})` : `color(display-p3 ${fmt(pr)} ${fmt(pg)} ${fmt(pb)})`; }
        case 'rec2020':
          if (!displayResult.rec2020) return '';
          { const rc = new Color(displayResult.hex); const rec = rc.to('rec2020'); const [rr,rg,rb] = rec.coords; return rec.alpha !== 1 ? `color(rec2020 ${fmt(rr)} ${fmt(rg)} ${fmt(rb)} / ${fmt(rec.alpha)})` : `color(rec2020 ${fmt(rr)} ${fmt(rg)} ${fmt(rb)})`; }
        default: return '';
      }
    },
    [displayResult, applyDecimalPrecision],
  );

  const gamutPreview = useMemo(() => {
    if (!displayResult) return { color: '#000000', label: 'sRGB', warning: null as string | null };
    const ct = precisionSettings.gamutClampMode === 'auto' ? selectedGamut : precisionSettings.gamutClampMode;
    if (selectedGamut === 'p3') {
      if (displayResult.displayP3) return { color: colorEngine.clampHexToGamut(displayResult.displayP3, ct), label: 'Display-P3', warning: null as string | null };
      return { color: displayResult.hex, label: 'Display-P3', warning: 'Display-P3 preview unavailable; falling back to sRGB.' };
    }
    if (selectedGamut === 'rec2020') {
      if (displayResult.rec2020) return { color: colorEngine.clampHexToGamut(displayResult.rec2020, ct), label: 'Rec.2020', warning: null as string | null };
      return { color: displayResult.hex, label: 'Rec.2020', warning: 'Rec.2020 preview unavailable; falling back to sRGB.' };
    }
    return { color: colorEngine.clampHexToGamut(displayResult.hex, ct), label: 'sRGB', warning: null as string | null };
  }, [displayResult, precisionSettings.gamutClampMode, selectedGamut]);

  const value: ColorContextType = {
    input, setInput, inputRef,
    result, displayResult, parseResult, error,
    getFormatLabel,
    selectedGamut, setSelectedGamut,
    selectedCmykProfile, handleSelectProfile,
    isFavorited, toggleFavorite,
    viewMode, setViewMode,
    copiedField, copyToClipboard,
    eyeDropperSupported, openEyeDropper, eyeDropperError,
    applyDecimalPrecision, formatCmyk, formatValue, getCssFunctional,
    pantoneState,
    precisionSettings,
    gamutPreview,
  };

  return <ColorContext.Provider value={value}>{children}</ColorContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useColorContext() {
  const ctx = useContext(ColorContext);
  if (!ctx) throw new Error('useColorContext must be used within a ColorProvider');
  return ctx;
}
